/**
 * Extracts the date fragment (e.g. "Thu, Nov 4") from a shipToHome message
 * like "Delivery by Thu, Nov 4 to 94102" by dropping any trailing " to {ZIP}"
 * segment and stripping a leading "Delivery by" or "By" verb.
 *
 * @param {string | undefined | null} message - The raw shipToHome message.
 * @returns {string} The bare date fragment, or an empty string if `message`
 *     is falsy.
 */
const stripLeadingVerb = message => {
    if (!message) {
        return '';
    }

    const trimmed = String(message).trim();
    const beforeTo = trimmed.split(/\s+to\s+/i)[0];

    return beforeTo.replace(/^(delivery\s+by|by)\s+/i, '').trim();
};

/**
 * Formats the ETA shown on the Free Shipping card (SHP-5111).
 * Produces "Delivery By {day}, {month} {date}" — e.g. "Delivery By Thu, Nov 4".
 *
 * @param {string | undefined | null} shipToHomeTitle - The raw shipToHome
 *     message from the Redux `shipToHomeSelector`.
 * @returns {string} The formatted card ETA, or an empty string if the input
 *     yields no date fragment.
 */
export const formatCardEta = shipToHomeTitle => {
    const dateFragment = stripLeadingVerb(shipToHomeTitle);

    return dateFragment ? `Delivery By ${dateFragment}` : '';
};

/**
 * Formats the ETA shown in the shipping details panel (SHP-5111).
 * Returns the shipToHome message minus its trailing " to {ZIP}" segment so
 * the ZIP can be rendered separately as a clickable trigger for the location
 * modal. Example: given "Delivery by Thu, Feb 14 to 94102" returns
 * "Delivery by Thu, Feb 14". The full visible panel line reads
 * "Delivery by Thu, Feb 14 to 94102".
 *
 * @param {string | undefined | null} shipToHomeMessage - The raw shipToHome
 *     message from the Redux `shipToHomeSelector`.
 * @returns {string} The formatted panel ETA, or an empty string if the input
 *     is falsy.
 */
export const formatPanelEta = shipToHomeMessage => {
    if (!shipToHomeMessage) {
        return '';
    }

    return String(shipToHomeMessage)
        .split(/\s+to\s+/i)[0]
        .trim();
};
